// Складовата карта на продукт в склад (`GET /inventory/stock/card`, BE с
// редизайна): началното салдо към началото на периода, движенията в периода с
// документа им и крайното салдо + замените на продукта. Заварената хронология
// `/stock/history` (цялата история без документи) остава в BE за съвместимост,
// FE вече не я ползва (типът `StockHistoryEntry` — да). Договорът е в BE docs/inventory.md
// §Складова карта. Количествата и цените са `number` като във всички складови
// отговори — BE ги сериализира като float, не като Decimal низ.
import { api } from "./client";
import type { StockHistoryEntry } from "./inventory";

export type StockMovementType = StockHistoryEntry["type"];

// Документът, който се отваря от реда: при замяна — родителската
// инвентаризация/разнасяне; заварена замяна без родител остава "substitution".
export type StockCardDocumentType = StockMovementType;

// donor — изписан ВМЕСТО друг продукт; recipient — липсата му е покрита от друг
export type SubstitutionRole = "donor" | "recipient";

export interface StockCardEntry {
  movementId: number;
  documentId: number;
  documentType: StockCardDocumentType;
  documentNumber: string; // фактура → номерът ѝ, разнасяне → номерът на отчета, иначе id
  date: string;
  type: StockMovementType; // типът на самото движение
  quantityIn: number; // количеството на реда; при инвентаризация — от max(0, преди) до преброеното
  quantityOut: number;
  balanceAfter: number; // ЗНАКОВА наличност след реда (FE я показва клампната до 0)
  // Минусът от продажби (стар BE — липсват): разход — частта под нулата;
  // приход — покритото от минуса от същия ден / отписаният минус от минали дни
  soldWithoutStock?: number;
  coveredNegative?: number;
  negativeWrittenOff?: number;
  unitPrice: number; // себестойност на реда — винаги нето
  createdByName: string | null;
  reason: string | null;
  batchNumber: string | null;
  expiryDate: string | null;
  substitutionRole: SubstitutionRole | null;
  counterpartProductId: number | null;
  counterpartProductName: string | null;
  coveredQuantity: number | null; // покритото в мярката на ПОЛУЧАТЕЛЯ
  // инвентаризация с приготвени рецепти: колко от преброеното е в тях (стар BE — липсва)
  inRecipes?: number | null;
}

export interface StockCardSubstitution {
  movementId: number;
  documentId: number;
  documentType: StockCardDocumentType;
  documentNumber: string;
  date: string;
  role: SubstitutionRole;
  counterpartProductId: number;
  counterpartProductName: string;
  counterpartUnit: string | null;
  quantity: number; // в мярката на продукта от картата
  counterpartQuantity: number; // в мярката на другия продукт
  createdByName: string | null;
}

export interface StockCard {
  warehouseId: number;
  productId: number;
  unit: string | null;
  openingBalance: number;
  closingBalance: number;
  entries: StockCardEntry[];
  substitutions: StockCardSubstitution[];
}

export type StockCardParams = {
  warehouseId: number;
  productId: number;
  startDate: string; // ISO — началото на първия ден
  endDate: string; // ISO — краят на последния ден
};

export async function getStockCard(params: StockCardParams): Promise<StockCard> {
  const res = await api.get<StockCard>("/inventory/stock/card", { params });
  return res.data;
}
